/**
 * IR (Intermediate Representation) — typed document tree for the PDF a11y editor.
 *
 * Design: docs/design/spa-a11y-editor-phase3-power-editor-260430.md §3
 *
 * Public API:
 *   buildIRFromStructTree(structNodes)  — flat struct list → Document IR
 *   apply(ir, op)                       — pure reducer; returns new IR
 *   ROLES                               — enum-like set of valid PDF structure roles
 *   IRError                             — thrown on invalid mutations
 *
 * All functions are pure: inputs are not mutated; every output is a fresh object.
 *
 * Seam extraction (docs/epics/active/ts-unit-testability-seams-260623/):
 *   Pure types, constants, and logic are in ir-logic.ts (jsdom-free, Node-importable).
 *   This file keeps `apply()` and the reducer dispatcher, which cannot move to
 *   ir-logic.ts because reducers/* import from "../ir.js" — extracting apply()
 *   to ir-logic.ts would create a circular ir-logic→reducers→ir-logic cycle.
 *   All other symbols are re-exported from ir-logic.ts for backward compatibility.
 *
 * @module ir
 */
// OpKinds — generated discriminator-literal const table sourced from
// system-models/wire-contracts/editor-edit-op.schema.json (Phase 0 of
// docs/design/wire-contracts-migration-project.md). Imported so that a
// schema-side rename surfaces as a module-load error via the
// _AssertOpKinds block below, rather than a silent dispatch miss. Switch
// cases use string literals because TS narrowing of discriminated unions
// requires literal-typed case expressions.
import { OpKinds } from "./wire-contracts/editor-edit-op.js";
// Reducer imports for apply() dispatcher — listed here (top-of-file per
// import/first) but kept as a coherent group to keep the reducer surface
// auditable without a separate index file. See apply() below.
import { setAltTextReducer, setDecorativeReducer, setRoleReducer, setActualTextReducer, } from "./reducers/text-reducers.js";
import { setTitleReducer, setLangReducer, setAttestationReducer, clearAllTagsReducer, } from "./reducers/doc-reducers.js";
import { reorderReadingReducer, setBboxReducer, createElementReducer, deleteElementReducer, } from "./reducers/structure-reducers.js";
import { batchReducer } from "./reducers/batch-reducer.js";
import { IRError } from "./ir-logic.js";
export { IRError, ROLES, ROLES_SET, buildIRFromStructTree, findBlockById, mutateBlocksById, mutateInlineById, coerceRole, buildBlockForRole, serializeIRToStructList, } from "./ir-logic.js";
// Module-load assertion: schema-side rename of any case label below will
// trip the corresponding key lookup on OpKinds at import time. Keep the
// case labels in apply() and these assertion entries in sync.
const _AssertOpKinds = {
    "set-alt-text": OpKinds.SET_ALT_TEXT,
    "set-role": OpKinds.SET_ROLE,
    "set-actual-text": OpKinds.SET_ACTUAL_TEXT,
    "set-lang": OpKinds.SET_LANG,
    "reorder-reading": OpKinds.REORDER_READING,
    "set-bbox": OpKinds.SET_BBOX,
    "clear-all-tags": OpKinds.CLEAR_ALL_TAGS,
    "BATCH": OpKinds.BATCH,
};
void _AssertOpKinds;
// ─── apply (pure reducer dispatcher) ──────────────────────────────────────────
//
// Phase FB.3 of frontend-mv-architecture-260524.md: per-op reducer logic
// extracted to web/static/editor/reducers/{text,doc,structure}-reducers.ts.
// `apply` here is now a thin discriminator switch that delegates to the
// matching reducer; the reducer functions are independently seam-testable
// without booting the editor (see reducers/test/*.test.mjs).
//
// WHY apply() stays here (not in ir-logic.ts): the reducer files (e.g.,
// reducers/text-reducers.ts) import from "../ir.js". If apply() moved to
// ir-logic.ts, the dependency graph would become:
//   ir-logic.ts → reducers/text-reducers.ts → ../ir.js → ir-logic.ts
// — a circular import. Keeping apply() in ir.ts preserves the acyclic shape:
//   ir-logic.ts (pure, no reducer deps)
//   reducers/*.ts → ir.ts (for types/helpers)
//   ir.ts → ir-logic.ts + reducers/*.ts (applies them)
/**
 * Apply an op to the IR, returning `{ ir, dirtyPages }`. Input IR is never
 * mutated. Throws `IRError("UnknownOpKind", …)` for unrecognised op kinds.
 *
 * Phase FB.8 of frontend-mv-architecture-260524.md: signature changed from
 * `(ir, op) -> Document` to `(ir, op) -> ReducerResult` so the dispatcher
 * can route notifications to viewport-aware subscribers. Callers that
 * only need the new IR can read `.ir`; the dispatcher reads both fields.
 *
 * Dispatch contract: each `case` delegates to a single-purpose reducer
 * function in `reducers/`. Reducers are pure `(ir, op) → ReducerResult`
 * functions with no module-state, no DOM access, and no logging. Errors
 * thrown by a reducer propagate up unchanged.
 *
 * Per-op success-path contracts:
 *
 *   `"set-alt-text"` — updates `Figure.alt` for the block matching `op.structId`.
 *     `dirtyPages` = `{block.page}` (the page the figure lives on). No-op if
 *     `op.structId` is not found (dirtyPages = empty Set).
 *
 *   `"set-decorative"` — updates `Figure.decorative` for the block matching
 *     `op.structId`. `dirtyPages` = `{block.page}`.
 *
 *   `"set-role"` — coerces the block via `coerceRole(block, op.after)`. The
 *     block's kind may change (e.g., Paragraph → Figure). `dirtyPages` =
 *     `{block.page}` (post-mutation — page is preserved across coercion).
 *
 *   `"set-actual-text"` — updates `Figure.actualText` for the block matching
 *     `op.structId`. `dirtyPages` = `{block.page}`.
 *
 *   `"set-title"` — replaces `ir.title` with `op.after`.
 *     `dirtyPages` = `new Set([0])` (doc-level sentinel).
 *
 *   `"set-lang"` — replaces `ir.language` with `op.after`.
 *     `dirtyPages` = `new Set([0])`.
 *
 *   `"set-attestation"` — replaces `ir.attestation` with `op.after`.
 *     `dirtyPages` = `new Set([0])`.
 *
 *   `"reorder-reading"` — reorders blocks on `op.pageNum` so their structId
 *     sequence matches `op.after`. Blocks not listed in `op.after` are appended
 *     in their original order after the reordered set.
 *     `dirtyPages` = `{op.pageNum}`.
 *
 *   `"set-bbox"` — updates `block.bbox` (BboxXywh) for the block matching
 *     `op.structId`. `dirtyPages` = `{block.page}`.
 *
 *   `"create-element"` — inserts a new block (role → kind via `coerceRole`) at
 *     the position determined by `op.parentStructId`. If `parentStructId` is
 *     `null`, the block is appended to the end of `ir.blocks`.
 *     `dirtyPages` = `{op.page}`.
 *
 *   `"delete-element"` — removes the block matching `op.structId` from
 *     `ir.blocks` (including nested removal from List items).
 *     If the structId is not found the op is idempotent (`dirtyPages` = empty Set).
 *     Otherwise `dirtyPages` = `{block.page}` (pre-deletion page).
 *
 *   `"BATCH"` — folds `op.ops` sequentially: each sub-op is passed through
 *     `apply()` in order; the IR returned by sub-op i is the input to sub-op i+1.
 *     `dirtyPages` = union of all sub-op dirtyPages. Nested BATCH throws
 *     `IRError("NestedBatch", …)`.
 */
export function apply(ir, op) {
    switch (op.kind) {
        case "set-alt-text": return setAltTextReducer(ir, op);
        case "set-decorative": return setDecorativeReducer(ir, op);
        case "set-role": return setRoleReducer(ir, op);
        case "set-actual-text": return setActualTextReducer(ir, op);
        case "set-title": return setTitleReducer(ir, op);
        case "set-lang": return setLangReducer(ir, op);
        case "set-attestation": return setAttestationReducer(ir, op);
        case "reorder-reading": return reorderReadingReducer(ir, op);
        case "set-bbox": return setBboxReducer(ir, op);
        case "create-element": return createElementReducer(ir, op);
        case "delete-element": return deleteElementReducer(ir, op);
        case "clear-all-tags": return clearAllTagsReducer(ir, op);
        case "BATCH": return batchReducer(ir, op);
        default:
            throw new IRError("UnknownOpKind", `unknown op kind: "${op.kind}"`);
    }
}
